import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CheckCircle, FlaskConical, AlertTriangle, ShoppingCart, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { addRecentlyViewed } from '../components/RecentlyViewed';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import CrossSellPopup from '../components/CrossSellPopup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const getBenefitDetail = (benefit, productName) => {
  const b = benefit.toLowerCase();
  if (b.includes('fat') && b.includes('break')) return `${productName} may stimulate lipolysis — the natural process of breaking down stored fat into free fatty acids. This mechanism supports the body's ability to utilize fat as an energy source, which is being actively studied in metabolic research contexts.`;
  if (b.includes('fat') && (b.includes('formation') || b.includes('lipogenesis'))) return `Research suggests ${productName} may help inhibit lipogenesis, the metabolic pathway responsible for converting excess calories into stored fat. By modulating this process, it could support healthier body composition.`;
  if (b.includes('muscle') && (b.includes('growth') || b.includes('mass') || b.includes('lean'))) return `${productName} has been studied for its potential to support lean muscle tissue development. This may occur through enhanced protein synthesis, improved nitrogen retention, or supporting natural growth factor pathways involved in muscle repair and hypertrophy.`;
  if (b.includes('recovery') || b.includes('repair') || b.includes('healing')) return `${productName} is being researched for its role in tissue repair pathways. Studies suggest it may accelerate the body's natural healing processes by supporting collagen synthesis, reducing inflammation at injury sites, and enhancing cellular regeneration.`;
  if (b.includes('immune')) return `Research indicates ${productName} may modulate immune system function by supporting T-cell maturation, enhancing natural killer cell activity, or promoting healthy cytokine production. These mechanisms play crucial roles in maintaining the body's defense systems.`;
  if (b.includes('sleep') || b.includes('circadian')) return `${productName} has been studied for its potential effects on sleep quality and circadian rhythm regulation. Research suggests it may support deeper, more restorative sleep phases, which are essential for recovery, cognitive function, and overall well-being.`;
  if (b.includes('cognitive') || b.includes('memory') || b.includes('brain') || b.includes('mental') || b.includes('focus')) return `Studies on ${productName} suggest it may enhance cognitive function through mechanisms such as improved synaptic plasticity, enhanced neurotransmitter signaling, or neuroprotective effects. These pathways are fundamental to memory formation, learning, and mental clarity.`;
  if (b.includes('anti-aging') || b.includes('aging') || b.includes('wrinkle') || b.includes('skin') || b.includes('youthful')) return `${productName} is being researched for its anti-aging properties. It may work by supporting collagen production, enhancing skin elasticity, reducing oxidative stress, or modulating cellular senescence pathways — all of which contribute to a more youthful appearance.`;
  if (b.includes('inflammation') || b.includes('anti-inflam')) return `Research suggests ${productName} may help reduce inflammation through modulation of inflammatory cytokines and pathways. By managing the body's inflammatory response, it could support recovery, comfort, and long-term tissue health.`;
  if (b.includes('energy') || b.includes('metabolism') || b.includes('metabolic')) return `${productName} may support cellular energy production and metabolic efficiency. This can involve enhanced mitochondrial function, improved nutrient utilization, or optimization of metabolic pathways that govern how the body produces and uses energy.`;
  if (b.includes('collagen') || b.includes('elasticity')) return `Studies indicate ${productName} may promote collagen synthesis and improve skin elasticity. Collagen is the most abundant structural protein in the body and is essential for maintaining skin firmness, joint flexibility, and tissue integrity.`;
  if (b.includes('hormone') || b.includes('testosterone') || b.includes('growth hormone') || b.includes('gh release') || b.includes('gh secretion')) return `${productName} has been studied for its potential to support natural hormone production. Research suggests it may stimulate the pituitary gland or related pathways, promoting the body's own production of key hormones involved in growth, metabolism, and recovery.`;
  if (b.includes('appetite') || b.includes('weight') || b.includes('obesity')) return `Research on ${productName} suggests it may influence appetite regulation and weight management pathways. This could involve signaling in the hypothalamus, modulation of hunger hormones, or enhanced satiety signals that support healthier eating patterns.`;
  if (b.includes('blood sugar') || b.includes('insulin') || b.includes('glucose')) return `${productName} is being studied for its potential role in glucose metabolism and insulin sensitivity. Maintaining healthy blood sugar levels is fundamental to metabolic health, energy regulation, and long-term wellness.`;
  if (b.includes('neuroprotect')) return `Research indicates ${productName} may offer neuroprotective benefits by shielding neurons from oxidative stress, excitotoxicity, and other forms of damage. This is particularly relevant for maintaining cognitive health and supporting the nervous system over time.`;
  if (b.includes('anxiety') || b.includes('stress') || b.includes('mood') || b.includes('calm')) return `${productName} has been studied for its potential anxiolytic and mood-regulating properties. Research suggests it may modulate neurotransmitter systems involved in stress response and emotional well-being, supporting a calmer and more balanced state.`;
  if (b.includes('sexual') || b.includes('libido') || b.includes('arousal') || b.includes('erectile')) return `Research on ${productName} explores its potential effects on sexual function through modulation of melanocortin receptors or other neurological pathways. Studies suggest it may support arousal, desire, and overall sexual well-being in research contexts.`;
  if (b.includes('gut') || b.includes('intestin') || b.includes('digestive')) return `${productName} is being researched for its potential benefits to gastrointestinal health. It may support gut lining integrity, modulate inflammatory responses in the digestive tract, and promote healthy gut function.`;
  if (b.includes('cardio') || b.includes('heart') || b.includes('vascular')) return `Studies suggest ${productName} may support cardiovascular health by promoting healthy blood vessel function, supporting cardiac tissue maintenance, or modulating factors involved in heart health and circulation.`;
  if (b.includes('bone') || b.includes('joint') || b.includes('cartilage')) return `Research on ${productName} indicates it may support musculoskeletal health by promoting bone density, cartilage repair, or joint lubrication. These factors are essential for maintaining mobility and structural integrity.`;
  if (b.includes('telomere') || b.includes('dna') || b.includes('longevity') || b.includes('lifespan')) return `${productName} is being studied for its potential role in cellular longevity. Research suggests it may support telomere maintenance or DNA repair mechanisms, which are fundamental to healthy aging at the cellular level.`;
  if (b.includes('hair') || b.includes('follicle')) return `Studies on ${productName} suggest it may promote hair health by supporting follicle function, improving blood flow to the scalp, or modulating growth factors involved in the hair growth cycle.`;
  if (b.includes('tanning') || b.includes('melanin') || b.includes('pigment')) return `${productName} has been researched for its ability to stimulate melanogenesis — the natural production of melanin in the skin. This process is responsible for skin pigmentation and may offer photoprotective benefits.`;
  if (b.includes('fda') || b.includes('gras') || b.includes('safety')) return `${productName} has undergone safety evaluations and research assessments. Its favorable safety profile has been documented in published studies, supporting its use in controlled research settings.`;
  if (b.includes('botox') || b.includes('injection') || b.includes('non-invasive')) return `${productName} offers a non-invasive alternative approach being studied in research settings. Unlike injectable procedures, it may provide benefits through topical or subcutaneous administration, potentially reducing the need for more invasive methods.`;
  return `This benefit of ${productName} is supported by ongoing research in the field. Scientists continue to explore the mechanisms through which this compound may deliver these effects, with promising findings published in peer-reviewed journals. Consult with a qualified professional to learn more.`;
};

export const ProductDetailPage = () => {
  const { id } = useParams();
  const [peptide, setPeptide] = useState(null);
  const [expandedBenefit, setExpandedBenefit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [logoVariant, setLogoVariant] = useState('with-logo');
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedPrintStyle, setSelectedPrintStyle] = useState(null);
  const [activeImage, setActiveImage] = useState(null);
  const [crossSellProduct, setCrossSellProduct] = useState(null);
  const [showCrossSell, setShowCrossSell] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyStatus, setNotifyStatus] = useState(''); // '', 'sending', 'done', 'error'
  const { addToCart } = useCart();

  const handleNotifyMe = async () => {
    if (!notifyEmail.trim()) return;
    setNotifyStatus('sending');
    try {
      await axios.post(`${API}/stock-alerts`, { email: notifyEmail.trim(), product_id: peptide.id });
      setNotifyStatus('done');
    } catch {
      setNotifyStatus('error');
    }
  };

  const handleAddToCart = () => {
    if (peptide && !peptide.out_of_stock) {
      if (peptide.sizes && peptide.sizes.length > 0 && !selectedSize) {
        toast.error('Please select a size first');
        return;
      }
      if (peptide.print_styles && peptide.print_styles.length > 0 && !selectedPrintStyle) {
        toast.error('Please select a print style first');
        return;
      }
      if (peptide.sizes && selectedSize) {
        const label = selectedPrintStyle ? `${selectedPrintStyle} • Size ${selectedSize}` : `Size ${selectedSize}`;
        addToCart(peptide, { label });
        toast.success(`${peptide.name} (${label}) added to cart`);
        return;
      }
      const variant = peptide.no_logo_image
        ? {
            label: logoVariant === 'with-logo' ? 'With Logo' : 'Without Logo',
            image: logoVariant === 'with-logo' ? peptide.image : peptide.no_logo_image
          }
        : null;
      addToCart(peptide, variant);
      toast.success(`${peptide.name}${variant ? ` (${variant.label})` : ''} added to cart`);
      if (peptide.id === 'snap-8' && crossSellProduct && !crossSellProduct.out_of_stock) {
        setShowCrossSell(true);
      }
    }
  };

  useEffect(() => {
    if (!peptide) return;
    const nonResearch = ['Apparel', 'Accessories', 'Skincare'].includes(peptide.category);
    document.title = nonResearch
      ? `${peptide.name} ${peptide.dosage} | Canadian Wellness & Fitness`
      : `${peptide.name} ${peptide.dosage} | Research Peptides Canada | Canadian Wellness & Fitness`;
    return () => { document.title = 'Canadian Peptides | Research Peptides Canada | Canadian Wellness & Fitness'; };
  }, [peptide]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLogoVariant('with-logo');
    setSelectedSize(null);
    setSelectedPrintStyle(null);
    setActiveImage(null);
    const fetchPeptide = async () => {
      try {
        const response = await axios.get(`${API}/peptides/${id}`);
        setPeptide(response.data);
        addRecentlyViewed(response.data);
        if (response.data.id === 'snap-8') {
          const csRes = await axios.get(`${API}/peptides/hyaluronic-acid-serum`);
          setCrossSellProduct(csRes.data);
        }
      } catch (err) {
        setError('Product not found');
        console.error('Error fetching peptide:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPeptide();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner" data-testid="loading-spinner" />
      </div>
    );
  }

  if (error || !peptide) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center" data-testid="error-message">
          <AlertTriangle size={48} className="text-[#C41E3A] mx-auto mb-4" />
          <h2 className="font-['Oswald'] text-2xl text-white mb-4">Product Not Found</h2>
          <Link to="/">
            <Button className="bg-[#C41E3A] hover:bg-[#A01830]">
              Return to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const isApparelOrAccessory = peptide.category === 'Apparel' || peptide.category === 'Accessories' || peptide.category === 'Skincare';
  const seoTitle = isApparelOrAccessory
    ? `${peptide.name} ${peptide.dosage} | Canadian Wellness & Fitness`
    : `${peptide.name} ${peptide.dosage} | Research Peptides Canada | Canadian Wellness & Fitness`;
  const seoDesc = `${peptide.description || ''}`.slice(0, 158);
  const canonicalUrl = `https://canadianwellness.net/product/${peptide.id}`;
  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `${peptide.name} ${peptide.dosage}`,
    image: peptide.image,
    description: seoDesc,
    sku: peptide.id,
    brand: { '@type': 'Brand', name: 'Canadian Wellness & Fitness' },
    offers: {
      '@type': 'Offer',
      url: canonicalUrl,
      price: String(peptide.price || '').replace('$', ''),
      priceCurrency: 'CAD',
      availability: peptide.out_of_stock ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock'
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="product-detail-page">
      <meta name="description" content={seoDesc} />
      <link rel="canonical" href={canonicalUrl} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }} />
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link 
          to="/#products" 
          className="inline-flex items-center text-gray-400 hover:text-[#C41E3A] transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft size={20} className="mr-2" />
          <span className="text-sm uppercase tracking-wider font-semibold">Back to Catalog</span>
        </Link>
      </div>

      {/* Product Content */}
      <section className="py-8 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Product Image */}
            <div className="lg:col-span-5">
              <div className="relative border border-[#27272A] rounded-sm overflow-hidden">
                <img
                  src={activeImage || (peptide.no_logo_image && logoVariant === 'without-logo' ? peptide.no_logo_image : peptide.image)}
                  alt={isApparelOrAccessory ? `${peptide.name} — ${peptide.dosage} | Canadian Wellness & Fitness` : `${peptide.name} ${peptide.dosage} research peptide vial — Canadian Wellness & Fitness`}
                  className="w-full h-auto block"
                  data-testid="product-image"
                />
                <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: 'inset 0 0 40px rgba(0,0,0,0.3)' }} />
                {/* Category Badge */}
                <span className="absolute top-4 left-4 category-badge text-xs uppercase tracking-wider px-3 py-1 rounded-sm">
                  {peptide.category}
                </span>
              </div>
              {/* Image Gallery Thumbnails */}
              {peptide.extra_images && peptide.extra_images.length > 0 && peptide.category === 'Apparel' && (
                <div className="flex gap-3 mt-3" data-testid="image-gallery">
                  {[peptide.image, ...peptide.extra_images].map((img, idx) => {
                    const isActive = (activeImage || peptide.image) === img;
                    return (
                      <button
                        key={img}
                        onClick={() => setActiveImage(img)}
                        className={`w-20 h-20 border rounded-sm overflow-hidden transition-all duration-200 ${
                          isActive ? 'border-[#C41E3A]' : 'border-[#27272A] hover:border-gray-500'
                        }`}
                        data-testid={`gallery-thumb-${idx}`}
                      >
                        <img src={img} alt={`${peptide.name} view ${idx + 1}`} className="w-full h-full object-cover" />
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Product Details */}
            <div className="lg:col-span-7">
              <div className="sticky top-28">
                {/* Title & Dosage */}
                <h1 
                  className="font-['Oswald'] text-4xl md:text-5xl font-bold uppercase tracking-tight text-white mb-2"
                  data-testid="product-title"
                >
                  {peptide.name}
                </h1>
                <div className="flex items-center justify-between mb-6">
                  <p className="text-[#D4AF37] text-xl font-semibold" data-testid="product-dosage">
                    {peptide.dosage}
                  </p>
                  <div className="flex items-center gap-3">
                    {peptide.original_price && <span className="text-gray-500 text-xl line-through">{peptide.original_price}</span>}
                    <p className="text-[#C41E3A] text-3xl font-bold" data-testid="product-price">
                      {peptide.price}
                    </p>
                  </div>
                </div>

                {/* Logo Variant Selector */}
                {peptide.no_logo_image && (
                  <div className="mb-6" data-testid="logo-variant-selector">
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-semibold">Select Style</p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setLogoVariant('with-logo')}
                        className={`flex-1 border rounded-sm px-4 py-3 transition-all duration-200 ${
                          logoVariant === 'with-logo'
                            ? 'border-[#C41E3A] bg-[#C41E3A]/10 text-white'
                            : 'border-[#27272A] text-gray-400 hover:border-gray-500'
                        }`}
                        data-testid="variant-with-logo"
                      >
                        <span className="text-sm font-semibold uppercase tracking-wider">With Logo</span>
                      </button>
                      <button
                        onClick={() => setLogoVariant('without-logo')}
                        className={`flex-1 border rounded-sm px-4 py-3 transition-all duration-200 ${
                          logoVariant === 'without-logo'
                            ? 'border-[#C41E3A] bg-[#C41E3A]/10 text-white'
                            : 'border-[#27272A] text-gray-400 hover:border-gray-500'
                        }`}
                        data-testid="variant-without-logo"
                      >
                        <span className="text-sm font-semibold uppercase tracking-wider">Without Logo</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Print Style Selector */}
                {peptide.print_styles && peptide.print_styles.length > 0 && (
                  <div className="mb-6" data-testid="print-style-selector">
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-semibold">
                      Select Print Style {selectedPrintStyle && <span className="text-[#D4AF37]">— {selectedPrintStyle}</span>}
                    </p>
                    <div className="flex gap-3">
                      {peptide.print_styles.map((style) => (
                        <button
                          key={style}
                          onClick={() => setSelectedPrintStyle(style)}
                          className={`flex-1 border rounded-sm px-4 py-3 text-sm font-semibold uppercase tracking-wider transition-all duration-200 ${
                            selectedPrintStyle === style
                              ? 'border-[#C41E3A] bg-[#C41E3A]/10 text-white'
                              : 'border-[#27272A] text-gray-400 hover:border-gray-500'
                          }`}
                          data-testid={`print-style-${style.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                        >
                          {style}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Size Selector */}
                {peptide.sizes && peptide.sizes.length > 0 && (
                  <div className="mb-6" data-testid="size-selector">
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-semibold">
                      Select Size {selectedSize && <span className="text-[#D4AF37]">— {selectedSize}</span>}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {peptide.sizes.map((size) => {
                        const soldOut = (peptide.sold_out_sizes || []).includes(size);
                        return (
                        <button
                          key={size}
                          onClick={() => !soldOut && setSelectedSize(size)}
                          disabled={soldOut}
                          className={`min-w-[56px] border rounded-sm px-4 py-3 text-sm font-semibold uppercase tracking-wider transition-all duration-200 ${
                            soldOut
                              ? 'border-[#1a1a1a] text-gray-700 line-through cursor-not-allowed'
                              : selectedSize === size
                              ? 'border-[#C41E3A] bg-[#C41E3A]/10 text-white'
                              : 'border-[#27272A] text-gray-400 hover:border-gray-500'
                          }`}
                          data-testid={`size-option-${size}`}
                        >
                          {size}
                        </button>
                        );
                      })}
                    </div>
                    {(peptide.sold_out_sizes || []).length > 0 && (
                      <p className="text-gray-500 text-xs mt-2" data-testid="sold-out-sizes-note">
                        {peptide.sold_out_sizes.join(', ')} currently sold out
                      </p>
                    )}
                  </div>
                )}

                {/* Add to Cart */}
                <div className="flex gap-3 mb-8">
                  <button
                    onClick={handleAddToCart}
                    disabled={peptide.out_of_stock}
                    className={`flex-1 flex items-center justify-center gap-2 text-white font-bold uppercase tracking-wider py-3 rounded-sm transition-all duration-300 ${peptide.out_of_stock ? 'bg-gray-600 cursor-not-allowed' : 'bg-[#C41E3A] hover:bg-[#A01830] btn-glow'}`}
                    data-testid="add-to-cart-btn"
                  >
                    <ShoppingCart size={20} />
                    {peptide.out_of_stock ? 'Out of Stock' : 'Add to Cart'}
                  </button>
                  <Link
                    to="/cart"
                    className="flex items-center justify-center border border-[#27272A] hover:border-[#C41E3A] text-gray-400 hover:text-[#C41E3A] font-semibold uppercase tracking-wider px-6 py-3 rounded-sm transition-all duration-300"
                    data-testid="view-cart-btn"
                  >
                    View Cart
                  </Link>
                </div>

                {/* Usage note (e.g. bac water 28-day rule) */}
                {peptide.bac_water_note && (
                  <div className="bg-[#0A0A0A] border border-[#D4AF37]/60 rounded-sm p-5 mb-8 flex gap-3" data-testid="bac-water-note">
                    <AlertTriangle size={20} className="text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[#D4AF37] text-sm font-semibold uppercase tracking-wide mb-1">Important Usage Note</p>
                      <p className="text-gray-300 text-sm leading-relaxed">{peptide.bac_water_note}</p>
                    </div>
                  </div>
                )}

                {/* Back-in-stock alert signup */}
                {peptide.out_of_stock && (
                  <div className="bg-[#0A0A0A] border border-[#D4AF37]/60 rounded-sm p-5 mb-8" data-testid="stock-alert-box">
                    {notifyStatus === 'done' ? (
                      <p className="text-green-400 text-sm font-semibold flex items-center gap-2" data-testid="stock-alert-success">
                        <CheckCircle size={16} />
                        You&apos;re on the list! We&apos;ll email you the moment this is back in stock.
                      </p>
                    ) : (
                      <>
                        <p className="text-[#D4AF37] text-sm font-semibold mb-1">Want it when it&apos;s back?</p>
                        <p className="text-gray-400 text-xs mb-3">Drop your email and we&apos;ll notify you the moment this product is restocked.</p>
                        <div className="flex gap-2">
                          <input
                            type="email"
                            placeholder="your@email.com"
                            value={notifyEmail}
                            onChange={e => setNotifyEmail(e.target.value)}
                            className="flex-1 bg-[#121212] border border-[#27272A] focus:border-[#D4AF37] text-white text-sm px-4 py-2.5 rounded-sm outline-none transition-colors"
                            data-testid="stock-alert-email"
                          />
                          <button
                            onClick={handleNotifyMe}
                            disabled={notifyStatus === 'sending' || !notifyEmail.trim()}
                            className="bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider px-5 py-2.5 rounded-sm transition-colors whitespace-nowrap"
                            data-testid="stock-alert-submit"
                          >
                            {notifyStatus === 'sending' ? '...' : 'Notify Me'}
                          </button>
                        </div>
                        {notifyStatus === 'error' && (
                          <p className="text-[#C41E3A] text-xs mt-2" data-testid="stock-alert-error">Please enter a valid email address.</p>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Description */}
                <div className="mb-8">
                  <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-white mb-3">
                    Description
                  </h2>
                  <p className="text-gray-300 leading-relaxed" data-testid="product-description">
                    {peptide.description}
                  </p>
                </div>

                {/* Extra Images Gallery */}
                {peptide.extra_images && peptide.extra_images.length > 0 && peptide.category !== 'Apparel' && (
                <div className="mb-8">
                  <div className="space-y-4">
                    {peptide.extra_images.map((img) => (
                      <div key={img} className="rounded-sm overflow-hidden border border-[#27272A]">
                        <img
                          src={img}
                          alt={`${peptide.name} gallery`}
                          className="w-full h-auto object-contain"
                        />
                      </div>
                    ))}
                  </div>
                </div>
                )}

                {/* Benefits */}
                <div className="mb-8">
                  <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-white mb-3">
                    Potential Benefits
                  </h2>
                  <div className="space-y-2" data-testid="product-benefits">
                    {peptide.benefits.map((benefit, index) => (
                      <div key={benefit} className="border border-[#27272A] rounded-sm overflow-hidden">
                        <button
                          onClick={() => setExpandedBenefit(expandedBenefit === index ? null : index)}
                          className="w-full flex items-center justify-between text-left px-4 py-3 hover:bg-[#121212] transition-colors"
                          data-testid={`benefit-toggle-${index}`}
                        >
                          <div className="flex items-center">
                            <CheckCircle size={18} className="text-[#D4AF37] mr-3 flex-shrink-0" />
                            <span className="text-gray-200 text-sm">{benefit}</span>
                          </div>
                          <ChevronDown
                            size={16}
                            className={`text-gray-500 transition-transform duration-300 flex-shrink-0 ml-2 ${expandedBenefit === index ? 'rotate-180 text-[#D4AF37]' : ''}`}
                          />
                        </button>
                        {expandedBenefit === index && (
                          <div className="px-4 pb-4 pt-1 bg-[#0A0A0A] border-t border-[#1a1a1a]">
                            <p className="text-gray-400 text-xs leading-relaxed pl-8">
                              {getBenefitDetail(benefit, peptide.name)}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Research Notes */}
                <div className="mb-8">
                  <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-white mb-3">
                    Research Notes
                  </h2>
                  <div className="bg-[#0A0A0A] border-l-2 border-[#C41E3A] p-4 rounded-sm">
                    <div className="flex items-start">
                      <FlaskConical size={20} className="text-[#C41E3A] mr-3 mt-0.5 flex-shrink-0" />
                      <p className="text-gray-400 text-sm leading-relaxed" data-testid="product-research">
                        {peptide.research_notes}
                      </p>
                    </div>
                  </div>
                </div>


                {/* Usage */}
                <div className="mb-8">
                  <h2 className="font-['Oswald'] text-xl uppercase tracking-wide text-white mb-3">
                    Usage Information
                  </h2>
                  <p className="text-gray-400 text-sm leading-relaxed" data-testid="product-usage">
                    {peptide.usage}
                  </p>
                </div>

                {/* Storage - only show for peptides */}
                {peptide.category !== 'Hormonal' && peptide.category !== 'Skincare' && peptide.category !== 'Accessories' && peptide.category !== 'Apparel' && (
                <div className="mb-8 bg-[#121212] border border-[#27272A] rounded-sm p-4" data-testid="storage-notice">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={18} className="text-[#D4AF37] flex-shrink-0" />
                    <p className="text-gray-300 text-sm font-semibold">All peptides must be <span className="text-[#D4AF37]">refrigerated</span> upon receipt.</p>
                  </div>
                </div>
                )}

                {/* Disclaimer */}
                {peptide.category !== 'Apparel' && (
                <div className="disclaimer-box rounded-sm p-4" data-testid="product-disclaimer">
                  <div className="flex items-start">
                    <AlertTriangle size={18} className="text-[#D4AF37] mr-3 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-[#D4AF37] mb-1">
                        RESEARCH PURPOSES ONLY
                      </p>
                      <p className="text-xs text-[#D4AF37]/80 leading-relaxed">
                        This product is intended for research and educational purposes only. 
                        Not intended for human consumption. Not intended to diagnose, treat, 
                        cure, or prevent any disease.
                      </p>
                    </div>
                  </div>
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products CTA */}
      <section className="py-16 bg-[#0A0A0A] border-t border-[#27272A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Oswald'] text-2xl md:text-3xl font-bold uppercase tracking-tight text-white mb-4">
            Explore More Products
          </h2>
          <p className="text-gray-400 mb-8">
            Discover our complete catalog of research peptides.
          </p>
          <Link to="/#products">
            <Button 
              className="bg-[#C41E3A] hover:bg-[#A01830] text-white uppercase font-bold tracking-wider px-8 py-4 rounded-sm transition-all duration-300 btn-glow"
              data-testid="view-catalog-button"
            >
              View Full Catalog
            </Button>
          </Link>
        </div>
      </section>
      {showCrossSell && crossSellProduct && (
        <CrossSellPopup product={crossSellProduct} onClose={() => setShowCrossSell(false)} />
      )}
    </div>
  );
};

export default ProductDetailPage;
